/**
 * A carteira de UM cliente pelo extrato anexado na conversa (spec-004).
 *
 * Stateless: nada aqui vai para a store. O upload pertence a um cliente
 * identificado e é acompanhado por polling; quem espera é o card
 * (`fee-fixo-carteira-upload-card`), que lê a régua em
 * `constants/carteira-chat.js`. Esta camada faz uma requisição por chamada.
 *
 * ★ REST do módulo, e não a porta do agente. O anexo vive na TELA do chat, mas
 * o transporte é HTTP autenticado próprio (D1 da spec; ADR-001 da spec-005):
 * o LLM está fora deste caminho inteiro — nenhum `tool_use` espelhado, nenhuma
 * operação de protocolo nova. É isso que mantém a porta única de IA fechada
 * (invariante I-3, travada em `constants/URL.unit.js`).
 *
 * ★ Os limites de arquivo são conferidos AQUI antes de a requisição sair, e
 * repetem os do backend de propósito: o servidor continua sendo a autoridade
 * (é ele que recusa o que passar), mas mandar 21 MB por uma rede móvel para
 * receber um "não" é gastar o tempo do consultor. Um lugar só no front — a
 * tela não valida por conta própria, só mostra a mensagem que volta daqui.
 */
import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { feeFixo as endpoint } from '@modules/fee-fixo/constants/URL'
import { recusaDosArquivos } from '@modules/fee-fixo/constants/carteira-chat'

// Toda mensagem de erro diz a próxima ação (regra do repo). Aqui vale dobrado:
// o consultor costuma estar com o cliente na linha quando anexa o extrato.
const MSG_SEM_CLIENTE =
  'Escolha o cliente que vai receber esta carteira antes de enviar o extrato.'
const MSG_SEM_ARQUIVO =
  'Selecione ao menos um arquivo (extrato em PDF, print ou planilha) para enviar.'
const MSG_SEM_UPLOAD =
  'Envio não identificado. Anexe o extrato novamente pelo clipe da conversa.'
const MSG_SEM_PREVIEW =
  'Esta confirmação não tem um preview registrado — peça a leitura de novo e ' +
  'confirme a partir do card. Nada foi gravado.'
const MSG_ERRO_UPLOAD =
  'Não foi possível enviar o extrato. Tente novamente mais tarde ou contate o suporte.'
const MSG_ERRO_STATUS =
  'Não foi possível consultar o andamento da leitura. Tente novamente em instantes.'
const MSG_ERRO_CONFIRMAR =
  'Não foi possível gravar a carteira. Tente novamente mais tarde ou contate o suporte.'
const MSG_ERRO_CANCELAR =
  'Não foi possível cancelar este envio. Tente novamente em instantes.'

/**
 * Normaliza o que o `UploadFileV2` entrega numa lista de arquivos.
 *
 * Com `multiple`, ele emite um **FileList** quando há mais de um arquivo e um
 * **File solto** quando há um só (`upload-file-v2.vue:65-86`). Sem normalizar,
 * o array-like chegaria ao `connection.js` e seria serializado como texto —
 * multipart com zero arquivos, e o backend recusando por "nenhum arquivo".
 */
const comoLista = (files) => {
  if (!files) return []
  if (Array.isArray(files)) return files.filter(Boolean)
  if (typeof files.length === 'number') return Array.prototype.slice.call(files)
  return [files]
}

class ApiFeeFixoCarteiraChat extends APITemplate {
  /**
   * Envia o extrato de um cliente e recebe o `upload_id` que o card acompanha.
   *
   * @param {Object} opts
   * @param {number|string} opts.idClienteApp `app_clientes.id` — o cliente que
   *   vai RECEBER a carteira. Nunca opcional: é ele que decide de quem é o
   *   patrimônio, e a tabela-alvo (`app_dados_ativos_clientes`) não tem
   *   `id_escritorio` para corrigir um erro aqui.
   * @param {File|FileList|File[]} opts.files 1 a 10 arquivos.
   */
  async upload({ idClienteApp, files } = {}) {
    // Guards ANTES de montar o corpo: `id_cliente_app: undefined` viraria a
    // string "undefined" no FormData e chegaria ao backend como requisição
    // válida-porém-errada.
    if (!idClienteApp) {
      return this.buildErrorResponse({
        message: MSG_SEM_CLIENTE,
        value: null,
        status: 400,
      })
    }

    const arquivos = comoLista(files)
    if (!arquivos.length) {
      return this.buildErrorResponse({
        message: MSG_SEM_ARQUIVO,
        value: null,
        status: 400,
      })
    }
    // A régua é a MESMA que o modal usa na seleção (`recusaDosArquivos`, em
    // `constants/carteira-chat.js`) — uma cópia aqui divergiria dela no
    // primeiro formato novo, e a tela passaria a aceitar o que esta camada
    // recusa. Ela nomeia o arquivo: com dez anexos, "algum passou do limite"
    // faz o consultor tentar de novo às cegas.
    const recusa = recusaDosArquivos(arquivos)
    if (recusa) {
      return this.buildErrorResponse({
        message: recusa,
        value: null,
        status: 400,
      })
    }

    const r = await con.post(endpoint.carteiraChatUpload, {
      id_cliente_app: idClienteApp,
      files: arquivos,
    })
    return (
      r ||
      this.buildErrorResponse({
        message: MSG_ERRO_UPLOAD,
        value: null,
      })
    )
  }

  /**
   * O andamento do upload: `{status, cliente, ativos, nao_importaveis, total,
   * plano_zeramento, preview_id, erro, aviso}` (contrato §2).
   *
   * Devolve o envelope do backend INTACTO — não traduz `status`. Um estado
   * novo chegando aqui tem de continuar desconhecido para quem espera:
   * mapeado para `preview`, o card mostraria uma tabela vazia; mapeado para
   * `erro`, anunciaria falha que não houve.
   */
  async status({ uploadId } = {}) {
    if (!uploadId) {
      return this.buildErrorResponse({
        message: MSG_SEM_UPLOAD,
        value: null,
        status: 400,
      })
    }

    const r = await con.get(endpoint.carteiraChatStatus(uploadId))
    return (
      r ||
      this.buildErrorResponse({
        message: MSG_ERRO_STATUS,
        value: null,
      })
    )
  }

  /**
   * Grava o lote que o preview mostrou — e só ele (contrato §3).
   *
   * ★ Fail-closed também AQUI, e não só no `:disabled` do botão. O servidor
   * exige o `preview_id` (one-shot no Redis) e responde 409 sem ele; disparar
   * a requisição assim mesmo gastaria o clique do consultor para entregar um
   * não. O 409 legítimo — preview consumido, hash divergente — sobe como veio:
   * a frase do servidor diz o que aconteceu melhor que qualquer texto daqui.
   */
  async confirmar({ uploadId, previewId } = {}) {
    if (!uploadId) {
      return this.buildErrorResponse({
        message: MSG_SEM_UPLOAD,
        value: null,
        status: 400,
      })
    }
    if (!previewId) {
      return this.buildErrorResponse({
        message: MSG_SEM_PREVIEW,
        value: null,
        status: 400,
      })
    }

    const r = await con.post(endpoint.carteiraChatConfirmar(uploadId), {
      preview_id: previewId,
    })
    return (
      r ||
      this.buildErrorResponse({
        message: MSG_ERRO_CONFIRMAR,
        value: null,
      })
    )
  }

  /** Descarta o preview sem gravar nada e queima o `preview_id` (§4). */
  async cancelar({ uploadId } = {}) {
    if (!uploadId) {
      return this.buildErrorResponse({
        message: MSG_SEM_UPLOAD,
        value: null,
        status: 400,
      })
    }

    // Sem corpo: a rota não recebe nada além do `upload_id` da URL, e mandar
    // um objeto vazio só criaria um multipart sem partes.
    const r = await con.post(endpoint.carteiraChatCancelar(uploadId))
    return (
      r ||
      this.buildErrorResponse({
        message: MSG_ERRO_CANCELAR,
        value: null,
      })
    )
  }
}

const apiFeeFixoCarteiraChat = new ApiFeeFixoCarteiraChat()
export { apiFeeFixoCarteiraChat }
export default apiFeeFixoCarteiraChat
